// Operations > Availability (owner, 24 Oct 2026): the page's helpers. The engine works out every number
// (api/broker_availability.py - GET /ops/availability); nothing here recounts cars or bookings.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const LIMIT_MAX = 50;

// "2026-10-24" -> "24 Oct" (a calendar day: read as UTC so no zone moves it)
export const dayText = (iso) => {
    if (!iso) return '';
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? String(iso) : `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};
export const weekday = (iso) => {
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? '' : WEEKDAYS[d.getUTCDay()];
};
// a day column's head, kept narrow so 30 days fit the page: the day of the month (the month too on the first column and
// on the 1st), then the weekday
export const dayHead = (iso, i) => {
    const dd = String(iso).slice(8, 10);
    return { top: i === 0 || dd === '01' ? dayText(iso) : String(Number(dd)), sub: weekday(iso) };
};

export const spanText = (from, to) => (from === to ? dayText(from) : `${dayText(from)} - ${dayText(to)}`);

// a day's cell in the calendar: cars free, the last car (a new booking has no car), sold with no car - the colour as a
// style: the table's striped rows would cover a class
export const CELL = {
    ok: { style: { backgroundColor: '#dcfce7', color: '#14532d' }, label: 'Cars free' },
    last: { style: { backgroundColor: '#fcd34d', color: '#451a03' }, label: 'Last car: a new booking has no car' },
    short: { style: { backgroundColor: '#dc2626', color: '#ffffff' }, label: 'Sold with no car' },
    none: { style: { backgroundColor: '#e5e7eb', color: '#374151' }, label: 'No car of it at the airport' },
};

// a calendar cell's state: a model with no car counted at the airport (its only cars out of service) is never "the
// last car" - grey, unless a booking is already sold for it
export const cellState = (g, i) => (g.cars || g.free[i] < 0 ? g.states[i] : 'none');

// a model's second line: the booking sites' code, else Ejar's category
export const modelNote = (g) => g.code || g.category || '';

// the booking sites' groups (the nine Rentway groups): their own limits and the periods to close on Rentway
export const siteGroups = (groups) => (groups || []).filter((g) => g.code && Array.isArray(g.site_states));

// every site group's closed periods, one row each (the list to close on Rentway)
export const closeRows = (groups) => siteGroups(groups).flatMap((g) => (g.closed || []).map((r) => ({
    key: `${g.code}:${r.from}`, code: g.code, name: g.name, from: r.from, to: r.to, days: r.days, why: r.why || '',
})));

// the admin's edits -> {group: {cap, reserve}} of what changed; an error text when a value is not 0..50
export function numbersPayload(groups, edits) {
    const out = {};
    for (const g of siteGroups(groups)) {
        const e = (edits || {})[g.code] || {};
        for (const k of ['cap', 'reserve']) {
            if (e[k] === undefined || e[k] === null || String(e[k]) === String(g[k])) continue;
            const text = String(e[k]).trim();
            if (!/^\d+$/.test(text) || Number(text) > LIMIT_MAX) {
                return { error: `${g.name}: ${k === 'cap' ? 'booking sites limit' : 'branch keeps'} must be a whole number from 0 to ${LIMIT_MAX}` };
            }
            out[g.code] = { ...(out[g.code] || {}), [k]: Number(text) };
        }
    }
    return { numbers: out, count: Object.values(out).reduce((n, v) => n + Object.keys(v).length, 0) };
}

// "2026-10-26T06:30:00" -> "Mon 26 Oct · 06:30" (Dubai wall time as the engine sends it)
export const whenText = (iso) => (iso ? `${weekday(iso)} ${dayText(iso)} · ${String(iso).slice(11, 16)}` : '');

// a model's line in the summary: the first day it is sold with no car / at its last car over the 90 days
export function modelStatus(g, days) {
    const i = (g.states || []).indexOf('short');
    if (i >= 0) return { tone: 'danger', text: `Sold with no car on ${dayText((days || [])[i])}` };
    if (!g.cars) return { tone: 'neutral', text: 'No car of it at the airport' };
    const j = (g.states || []).indexOf('last');
    if (j >= 0) return { tone: 'warning', text: `Last car on ${dayText((days || [])[j])}` };
    return { tone: 'success', text: 'Cars free every day' };
}

export const modelRows = (groups, days) => (groups || []).map((g) => ({
    key: g.key, name: g.name, note: modelNote(g), cars: g.cars, ready: g.ready || 0, held: g.held || 0,
    out: (g.out || []).length, booked: (g.bookings || []).length,
    noCar: (g.bookings || []).filter((b) => b.no_car).length, status: modelStatus(g, days),
}));

// every booking at the airport, the soonest pick-up first; filters: model key, channel, words, only those with no car
export function bookingRows(groups, { model = '', channel = '', words = '', noCar = false } = {}) {
    const q = String(words || '').trim().toLowerCase();
    return (groups || [])
        .filter((g) => !model || g.key === model)
        .flatMap((g) => (g.bookings || []).map((b, i) => ({ ...b, model: g.name, note: modelNote(g), key: `${g.key}:${i}:${b.id}` })))
        .filter((b) => (!channel || b.channel === channel) && (!noCar || b.no_car)
            && (!q || [b.id, b.customer, b.channel, b.ref, b.model].some((v) => String(v || '').toLowerCase().includes(q))))
        .sort((a, b) => (a.pickup < b.pickup ? -1 : a.pickup > b.pickup ? 1 : String(a.id).localeCompare(String(b.id))));
}

// the channels the bookings came from, for the filter
export const channelList = (groups) => [...new Set((groups || []).flatMap((g) => (g.bookings || []).map((b) => b.channel)).filter(Boolean))].sort();

// the cars out on rent now: not back / no contract first, then the soonest back
export const outRows = (groups, model = '', words = '') => (groups || [])
    .filter((g) => !model || g.key === model)
    .flatMap((g) => (g.out || []).map((c, i) => ({ ...c, model: g.name, note: modelNote(g), key: `${g.key}:${i}:${c.plate}` })))
    .filter((c) => !words.trim() || [c.plate, c.customer, c.contract, c.model, c.channel].join(' ').toLowerCase().includes(words.trim().toLowerCase()))
    .sort((a, b) => ((a.state === 'rented') - (b.state === 'rented')) || String(a.back || '').localeCompare(String(b.back || '')));

export const OUT_STATE = {
    rented: { tone: 'info', text: 'On rent' },
    'not back': { tone: 'danger', text: 'Due back - not back yet' },
    'no contract': { tone: 'warning', text: 'Rented, no open contract' },
};

// one model's chart: a row per car out and per booking, a cell per day - 'out' / 'booking' while it holds a car that
// day, 'nocar' on the days the engine left the booking with no car, null otherwise
const overlaps = (from, to, day) => {
    const lo = `${day}T00:00:00`;
    const next = new Date(`${day}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    const hi = `${next.toISOString().slice(0, 10)}T00:00:00`;
    return (!from || from < hi) && (!to || to > lo);
};
export function chartRows(g, days) {
    const out = (g.out || []).map((c, i) => ({
        key: `out:${i}:${c.plate}`, title: `${c.plate} - on rent`,
        sub: c.state === 'rented' ? `back ${whenText(c.back)}` : OUT_STATE[c.state]?.text || c.state,
        cells: days.map((d) => (overlaps(null, c.state === 'rented' ? c.back : null, d) ? 'out' : null)),
    }));
    const booked = (g.bookings || []).map((b, i) => {
        const none = new Set(b.no_car_days || []);
        return {
            key: `bk:${i}:${b.id}`, title: `${b.id} - ${b.channel}${b.not_on_ejar ? ' (not on Ejar yet)' : ''}`,
            sub: `${b.customer ? `${b.customer} · ` : ''}${whenText(b.pickup)} → ${whenText(b.dropoff)}`,
            cells: days.map((d) => (none.has(d) ? 'nocar' : overlaps(b.pickup, b.dropoff, d) ? 'booking' : null)),
        };
    });
    return [...out, ...booked];
}

// one model's cars in words: "14 at the airport · 2 ready · 3 held for a booking (Maintenance) · 7 rented · 2 not back"
export function carsText(g) {
    const parts = [`${g.cars} at the airport`, `${g.ready} ready`];
    if (g.held) parts.push(`${g.held} held for a booking (Maintenance)`);
    parts.push(`${g.rented} rented`);
    if (g.overdue) parts.push(`${g.overdue} not back`);
    if (g.no_contract) parts.push(`${g.no_contract} rented without a contract`);
    Object.entries(g.left_out || {}).forEach(([status, n]) => parts.push(`${n} ${status} (left out)`));
    return parts.join(' · ');
}
